/**
 * AccessDenied Component
 *
 * Displayed when a user tries to access a resource they don't have permission for.
 * Can be used as a full page or as an inline component.
 *
 * Usage:
 *   // As a full page (in routes)
 *   <Route path="/access-denied" element={<AccessDenied />} />
 *
 *   // As inline fallback
 *   <ProtectedRoute segment="billing" fallback={<AccessDenied inline />}>
 *     <BillingPage />
 *   </ProtectedRoute>
 */

import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import GlobalStyles from '../../components/component-items/styles'
import { BusinessSegment, FunctionRole } from '../../types/rbac'

// Styled components matching the error page style
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`

const InlineContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`

const Icon = styled.div`
  font-size: 4em;
  margin-bottom: 20px;
`

const Title = styled.h1`
  color: #333;
  margin-bottom: 16px;
  font-size: 2em;
`

const Message = styled.p`
  color: #666;
  margin-bottom: 24px;
  line-height: 1.6;
`

const RequiredInfo = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: left;
`

const RequiredLabel = styled.span`
  font-weight: 600;
  color: #333;
`

const RequiredValue = styled.span`
  color: #666;
  text-transform: capitalize;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`

// Segment display names
const SEGMENT_LABELS: Record<BusinessSegment, string> = {
  outbound: 'Outbound / Shipping',
  inbound: 'Inbound / Receiving',
  inventory: 'Inventory',
  products: 'Products',
  vas: 'VAS / Projects',
  billing: 'Billing',
  postage: 'Postage',
  integrations: 'Integrations',
  settings: 'Settings',
  reports: 'Reports',
}

// Level display names
const LEVEL_LABELS: Record<FunctionRole, string> = {
  none: 'No Access',
  viewer: 'Viewer',
  operator: 'Operator',
  admin: 'Admin',
}

interface LocationState {
  from?: string
  required?: {
    segment?: BusinessSegment
    minLevel?: FunctionRole
    segments?: [BusinessSegment, FunctionRole][]
  }
}

interface AccessDeniedProps {
  /**
   * If true, renders as an inline component (no full-page styling)
   */
  inline?: boolean

  /**
   * Custom title
   */
  title?: string

  /**
   * Custom message
   */
  message?: string
}

export function AccessDenied({
  inline = false,
  title = 'Access Denied',
  message = "You don't have permission to view this page.",
}: AccessDeniedProps): React.ReactElement {
  const location = useLocation()
  const navigate = useNavigate()
  const state = location.state as LocationState | undefined

  const handleGoBack = () => {
    navigate(-1)
  }

  const content = (
    <>
      <Icon>🔒</Icon>
      <Title>{title}</Title>
      <Message>{message}</Message>

      {state?.required && (
        <RequiredInfo>
          <p style={{ marginBottom: 8, fontWeight: 600 }}>Required Permission:</p>
          {state.required.segment && (
            <p>
              <RequiredLabel>Segment: </RequiredLabel>
              <RequiredValue>{SEGMENT_LABELS[state.required.segment] || state.required.segment}</RequiredValue>
              <br />
              <RequiredLabel>Minimum Level: </RequiredLabel>
              <RequiredValue>{LEVEL_LABELS[state.required.minLevel || 'viewer']}</RequiredValue>
            </p>
          )}
          {state.required.segments && (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {state.required.segments.map(([seg, level], idx) => (
                <li key={idx}>
                  {SEGMENT_LABELS[seg] || seg}: {LEVEL_LABELS[level]}
                </li>
              ))}
            </ul>
          )}
        </RequiredInfo>
      )}

      <Message style={{ fontSize: '0.9em' }}>Contact your administrator if you believe this is an error.</Message>

      <ButtonGroup>
        <GlobalStyles.Button onClick={handleGoBack} style={{ minWidth: 100 }}>
          Go Back
        </GlobalStyles.Button>
        <Link to="/">
          <GlobalStyles.Button className="secondary" style={{ minWidth: 100 }}>
            Home
          </GlobalStyles.Button>
        </Link>
      </ButtonGroup>
    </>
  )

  if (inline) {
    return <InlineContainer>{content}</InlineContainer>
  }

  return (
    <Container>
      <Card>{content}</Card>
    </Container>
  )
}

export default AccessDenied
